<template>
  <div class="popup z-index-10-fg confirmation small-popup edit-user-popup" ref="popupWindow">
    <div class="loading-container" v-if="loading"><img src="../assets/spinner.svg" /><img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" /></div>
    <div class="popup-content" ref="popupContent" v-if="!loading">
      <div class="popup-header">
        <h2>User Information & Settings</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div class="popup-body">
        <TextBox v-model="user.FIRSTNAME" label="First Name" class="input-item medium-item" :invalid="showInvalid && !user.FIRSTNAME" />
        <TextBox v-model="user.LASTNAME" label="Last Name" class="input-item medium-item" />
        <TextBox v-model="user.EMAIL" label="Email" class="input-item medium-item" :invalid="showInvalid && (!user.EMAIL || !isValidEmail(user.EMAIL))" />
        <Dropdown
          v-model="user.STARTPAGE"
          :canBeEmpty="true"
          label="Start Page"
          :options="startPages"
          class="input-item small-item"
          :invalid="showInvalid && (!user.STARTPAGE || !startPages.find((page) => page.value === user.STARTPAGE))" />
        <Dropdown v-model="user.LOCATION" label="Location" :options="locations" class="input-item small-item" />
        <Dropdown
          v-model="user.OOO"
          label="Out of Office"
          :options="[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]"
          class="input-item small-item" />
        <TextBox v-model="user.BACKINOFFICE" label="Back in Office" :readonly="!user.OOO" type="date" class="input-item small-item" :invalid="showInvalid && !user.BACKINOFFICE && user.OOO" />
      </div>
      <div class="sub-tables">
        <div class="sub-table input-item medium-item">
          <div class="sub-table-header">
            <h6>Roles</h6>
          </div>
          <div class="sub-table-row">
            <span>Inside Sales</span>
            <Checkbox v-model="user.INSIDESALES" />
          </div>
          <div class="sub-table-row indented" v-if="user.INSIDESALES">
            <span>Auto Assign Leads</span>
            <Checkbox v-model="user.ASSIGNLEADS" />
          </div>
          <div class="sub-table-row">
            <span>Outside Sales</span>
            <Checkbox v-model="user.OUTSIDESALES" />
          </div>
        </div>
        <div class="sub-table input-item medium-item">
          <div class="sub-table-header">
            <h6>Modules</h6>
          </div>
          <div class="sub-table-row">
            <span>Leads</span>
            <Checkbox v-model="user.SHOWLEADS" />
          </div>
          <div class="sub-table-row">
            <span>Contacts</span>
            <Checkbox v-model="user.SHOWCONTACTS" />
          </div>
          <div class="sub-table-row">
            <span>Reports</span>
          </div>
          <div class="sub-table-row indented">
            <span>General Reports</span>
            <Checkbox v-model="user.SHOWGENERALREPORTS" />
          </div>
          <div class="sub-table-row indented">
            <span>Sales Impact Report</span>
            <Checkbox v-model="user.SHOWIMPACTREPORT" />
          </div>
        </div>
        <div class="sub-table input-item medium-item">
          <div class="sub-table-header">
            <h6>Administrative Privileges</h6>
          </div>
          <div class="sub-table-row">
            <span>Contacts</span>
            <Checkbox v-model="user.CONTACTSADMIN" />
          </div>
          <div class="sub-table-row">
            <span>Leads</span>
            <Checkbox v-model="user.LEADSADMIN" />
          </div>
          <div class="sub-table-row">
            <span>Users</span>
            <Checkbox v-model="user.ADMIN" />
          </div>
        </div>
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">Close</button>
        <button class="text-btn blue" @click="resetMFA(user.USERID)">Reset MFA</button>
        <button :class="{ 'square-btn': true, saving: isSaving }" @click="saveUser()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import useUserQuery from '@/composables/useUserQuery';
import TextBox from '@/components/inputs/TextBox.vue';
import Dropdown from '@/components/inputs/Dropdown.vue';
import Checkbox from '@/components/inputs/Checkbox.vue';
import useAlerts from '@/composables/useAlerts';
import useUserMutate from '@/composables/useUserMutate';
import useUsersQuery from '@/composables/useUsersQuery';
import { resetMFA } from '@/composables/useAuthMutate';
import { watch, ref } from 'vue';

export default {
  name: 'EditUser',
  emits: ['closePopup', 'save'],
  components: {
    TextBox,
    Dropdown,
    Checkbox,
  },
  props: {
    userid: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { getUser } = useUserQuery();
    const { addAlert } = useAlerts();
    const { updateUser, addUser } = useUserMutate();
    const { getUsers } = useUsersQuery();

    const stuffToReturn = {
      addAlert,
      updateUser,
      addUser,
      getUsers,
      resetMFA,
    };

    if (props.userid == 'new') {
      const user = ref({
        FIRSTNAME: '',
        LASTNAME: '',
        EMAIL: '',
        STARTPAGE: '',
        LOCATION: '',
        OOO: false,
        BACKINOFFICE: '',
        INSIDESALES: false,
        ASSIGNLEADS: false,
        OUTSIDESALES: false,
        SHOWLEADS: false,
        SHOWCONTACTS: true,
        SHOWGENERALREPORTS: false,
        SHOWIMPACTREPORT: false,
        CONTACTSADMIN: false,
        LEADSADMIN: false,
        ADMIN: false,
      });

      return { user, loading: false, ...stuffToReturn };
    } else {
      const { user, loading, error } = getUser(props.userid);

      watch(error, (newVal) => {
        if (newVal) {
          emit('closePopup');
        }
      });

      return { user, loading, ...stuffToReturn };
    }
  },
  data() {
    return {
      isSaving: false,
      locations: ['Rickreall', 'Tumwater', 'Swift River'],
      showInvalid: false,
    };
  },
  computed: {
    startPages() {
      const startPage = [];
      if (this.user.SHOWLEADS) {
        startPage.push({ text: 'Leads', value: 'leads' });
      }
      if (this.user.SHOWCONTACTS) {
        startPage.push({ text: 'Contacts', value: 'contacts' });
      }
      if (this.user.SHOWIMPACTREPORT) {
        startPage.push({ text: 'Sales Impact Report', value: 'salesImpact' });
      }
      return startPage;
    },
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validate() {
      this.showInvalid = false;
      if (!this.user.FIRSTNAME) {
        this.showInvalid = true;
        this.addAlert('Please enter a valid first name', 'high', 3);
        return false;
      }
      if (!this.user.EMAIL) {
        this.showInvalid = true;
        this.addAlert('Please enter a valid email', 'high', 3);
        return false;
      }
      if (!this.isValidEmail(this.user.EMAIL)) {
        this.showInvalid = true;
        this.addAlert('Please enter a valid email address', 'high', 3);
        return false;
      }
      if (!this.user.SHOWLEADS && !this.user.SHOWCONTACTS && !this.user.SHOWGENERALREPORTS && !this.user.SHOWIMPACTREPORT) {
        this.showInvalid = true;
        this.addAlert('Please select at least one module', 'high', 3);
        return false;
      }
      if (!this.user.STARTPAGE || !this.startPages.find((page) => page.value === this.user.STARTPAGE)) {
        this.showInvalid = true;
        this.addAlert('Please enter a valid start page', 'high', 3);
        return false;
      }
      if (this.user.OOO && !this.user.BACKINOFFICE) {
        this.showInvalid = true;
        this.addAlert('Please enter a valid back in office date', 'high', 3);
        return false;
      }
      return true;
    },
    async saveUser() {
      if (!this.validate()) return;
      this.isSaving = true;

      if (this.userid === 'new') {
        const response = await this.addUser(this.user);
        if (response) {
          await this.getUsers();
          this.addAlert('User added successfully', 'low', 2);
          this.$emit('closePopup');
        }
      } else {
        const response = await this.updateUser(this.userid, this.user);
        if (response) {
          await this.getUsers();
          this.addAlert('User updated successfully', 'low', 2);
          this.$emit('closePopup');
        }
      }

      this.isSaving = false;
    },
  },
  watch: {
    'user.INSIDESALES': function (newVal) {
      if (!newVal) {
        this.user.ASSIGNLEADS = false;
      }
    },
    'user.OOO': function (newVal) {
      if (!newVal) {
        this.user.BACKINOFFICE = '';
      } else {
        this.user.BACKINOFFICE = new Date().toISOString().split('T')[0];
      }
    },
    startPages: function (newVal) {
      if (newVal.length === 0) {
        this.user.STARTPAGE = '';
      } else if (newVal.length === 1) {
        this.user.STARTPAGE = newVal[0].value;
      } else {
        // If current STARTPAGE is not in the new options, set to the first one
        const found = newVal.find((opt) => opt.value === this.user.STARTPAGE);
        if (!found) {
          this.user.STARTPAGE = newVal[0].value;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-user-popup.small-popup {
  width: 55rem;
  max-width: 80vw;

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;
    margin-bottom: 1.5rem;

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 18%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }

      &.medium-item {
        min-width: 10rem;
        flex: 1 0 30%;
      }
    }
  }

  .sub-tables {
    display: flex;
    flex-direction: row;
    margin-bottom: 2.5rem;
    margin-top: 1.5rem;
    gap: 2.5rem;
    width: 100%;
    align-items: flex-start;

    .sub-table {
      border: 1px solid $inputBorderColor;
      border-radius: $borderRadius4;
      flex: 1;

      h6 {
        font-size: $fontSize14;
        font-weight: 600;
        color: $textColor87;
        margin: 0;
        padding: 0.8rem 1rem;
      }

      .sub-table-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid $inputBorderColor;
        padding: 0.8rem 1rem;
        font-size: $fontSize14;

        &.indented {
          padding-left: 1.5rem;
        }
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 25rem;

    img {
      width: 2rem;
      height: 2rem;
    }
  }
}
</style>
